<template>
  <ControlWrapper
    v-if="!hasAllProperties"
    v-bind="controlWrapper"
    :styles="styles"
    :applied-options="appliedOptions"
    :error="control.errors"
  >
    <SectionWrapper :title="title" :title-class="styles.control.label">
      <div class="flex w-full flex-col gap-8">
        <form v-if="!hasAllUserProperties" class="flex w-full flex-col gap-4">
          <WInput
            v-model="user.firstName"
            type="text"
            name="firstName"
            autocomplete="given-name"
            subtitle
            :label="controlOptions.properties?.selectedUser.properties.firstName.title || 'First name'"
            :error="showErrors && !user.firstName && isRequiredProperty('firstName') ? 'This field is required' : ''"
            @update:model-value="
              capitaliseName();
              updateData('user');
            "
          >
          </WInput>

          <WInput
            v-model="user.lastName"
            type="text"
            name="lastName"
            autocomplete="family-name"
            :label="controlOptions.properties?.selectedUser.properties.lastName.title"
            subtitle
            @update:model-value="
              capitaliseName();
              updateData('user');
            "
            :error="showErrors && !user.lastName && isRequiredProperty('lastName') ? 'This field is required' : ''"
          />

          <WInput
            v-if="!!controlOptions.properties?.selectedUser?.properties?.email"
            v-model="user.email"
            type="email"
            inputmode="email"
            name="email"
            autocmplete="email"
            :label="controlOptions.properties?.selectedUser?.properties?.email?.title"
            subtitle
            @update:model-value="
              (event) => {
                validateEmail(event);
                updateData('user');
              }
            "
            :error="
              emailError || (showErrors && !user.email && isRequiredProperty('email') ? 'This field is required' : '')
            "
          />

          <PhoneInput
            :phone="user.phone.formatted"
            subtitle
            :label="controlOptions.properties?.selectedUser.properties.phone.title"
            placeholder=""
            class="text-base"
            inner-class="vehicle-phone-input"
            @phone-changed="
              (event) => {
                phoneChange(event);
                updateData('user');
              }
            "
            @error="(hasError) => (phoneFormatError = hasError ? 'Invalid phone number' : '')"
            :error-message="
              phoneError ||
              (showErrors && !user.phone.formatted && isRequiredProperty('phone') ? 'This field is required' : '')
            "
          />
        </form>
        <div v-if="!hasAllInsuranceProperties" class="w-full">
          <p v-if="!hasAllUserProperties" :class="styles.control.label">Insurance details</p>
          <div class="flex w-full flex-col gap-4">
            <div>
              <WInput
                v-model="insurance.provider"
                subtitle
                type="text"
                name="policy"
                label="Provider"
                :error="showErrors && !insurance.provider ? 'This field is required' : ''"
                @update:model-value="updateData('insurance')"
              />
            </div>
            <WInput
              v-model="insurance.number"
              subtitle
              type="text"
              name="policy"
              label="Policy Number"
              :error="showErrors && !insurance.number ? 'This field is required' : ''"
              @update:model-value="updateData('insurance')"
            />
          </div>
        </div>
      </div>
    </SectionWrapper>
  </ControlWrapper>
  <PhoneInput v-if="hasAllProperties" :phone="user.phone.formatted" hidden @phone-changed="phoneChange" />
</template>

<script lang="ts">
  import { defineComponent, nextTick, ref, watch, onBeforeUnmount, computed, onMounted, inject } from 'vue';
  import { ControlElement, JsonFormsSubStates } from '@jsonforms/core';
  import { rendererProps, useJsonFormsControl, RendererProps } from '@jsonforms/vue';
  import { useWidgetControl } from '../util';
  import { capitalise, validateEmail } from 'src/utils/helpers';
  import { Phone } from 'src/types/overlays';
  import ControlWrapper from './ControlWrapper.vue';
  import SectionWrapper from '../../claims-widget/components/SectionWrapper.vue';
  import WInput from '../../base/Input.vue';
  import PhoneInput from '../../base/PhoneInput.vue';
  import { config } from 'src/utils/config';
  import { useWidgetError } from '../util/composition';
  import { InsuranceConfig, PublicUserConfig, StepType } from 'src/types/widget';
  import type { MissingProperties as IMissingProperties, UserConfig } from '../../../types/widget';
  import { getClarity, sendAnalytics } from 'src/utils/analytics';
  import { setUserData } from 'src/plugins/openreplay';

  export interface PropertyValue {
    title: string;
    type: string;
  }

  export interface InsuranceProperty {
    provider: PropertyValue;
    number: PropertyValue;
  }

  export interface SelectedInsurance {
    title: string;
    label: string;
    type: string;
    properties: InsuranceProperty;
  }

  export interface UserProperty {
    firstName: PropertyValue;
    lastName: PropertyValue;
    email: PropertyValue;
    phone: PropertyValue;
  }

  export interface SelectedUser {
    required: Array<keyof UserProperty>;
    type: string;
    title: string;
    label: string;
    properties: UserProperty;
  }

  export interface Property {
    selectedInsurance: SelectedInsurance;
    selectedUser: SelectedUser;
  }

  export interface MissingPropertiesConfig {
    required: Array<keyof Property>;
    missingProperties: true;
    type: 'object';
    title: string;
    properties: Property;
  }

  export default defineComponent({
    components: {
      ControlWrapper,
      SectionWrapper,
      WInput,
      PhoneInput,
    },
    props: {
      ...rendererProps<ControlElement>(),
    },
    setup(props: RendererProps<ControlElement>) {
      const { onValidate, errors } = useWidgetError();
      const control = useWidgetControl(useJsonFormsControl(props));
      const jsonforms = inject<JsonFormsSubStates>('jsonforms');
      const showErrors = ref(false);
      const listener = onValidate(() => {
        showErrors.value = true;
      });
      onBeforeUnmount(listener.off);
      const controlData = computed<Partial<IMissingProperties>>(() => control.control.value?.data);
      const controlOptions = computed(() => control.control.value?.schema as Partial<MissingPropertiesConfig>);
      const requiredUserProperties = computed(() => controlOptions.value.properties?.selectedUser.required || []);
      const user = ref<Omit<PublicUserConfig, 'phone'> & { phone: Phone }>(
        controlData.value?.selectedUser
          ? {
              ...controlData.value.selectedUser,
              phone: getFormattedPhone(controlData.value.selectedUser.phone),
            }
          : config.value?.driver
            ? {
                ...config.value.driver,
                phone: getFormattedPhone(config.value.driver.phone),
              }
            : {
                firstName: '',
                lastName: '',
                email: '',
                phone: {
                  formatted: '',
                },
              }
      );
      const insurance = ref<InsuranceConfig>(
        controlData.value?.selectedInsurance ||
          JSON.parse(
            JSON.stringify({
              provider: config.value?.insurance?.provider,
              number: config.value?.insurance?.number,
            })
          ) || {
            number: undefined,
            provider: undefined,
          }
      );
      const hasAllUserProperties = ref(
        config.value.settings?.type !== StepType.CLAIMS || controlData.value?.isModified?.includes('user')
          ? false
          : !!(user.value.firstName && user.value.lastName && user.value.email && user.value.phone?.formatted)
      );

      const hasAllInsuranceProperties = ref(
        controlData.value?.isModified?.includes('insurance')
          ? false
          : config.value.settings?.type !== StepType.CLAIMS || !!(insurance.value.number && insurance.value.provider)
      );

      const hasAllProperties = ref(
        config.value.settings?.type !== StepType.CLAIMS || !!controlData.value?.isModified?.length
          ? false
          : hasAllUserProperties.value && hasAllInsuranceProperties.value
      );
      const emailError = ref('');
      const licensePlateError = ref('');
      const phoneFormatError = ref(''); // Phone format validation from PhoneInput
      const phoneMatchError = ref(''); // Custom validation: phone matches driver
      const phoneError = computed(() => {
        // Show format error first, then match error
        return phoneFormatError.value || phoneMatchError.value;
      });
      const title = ref(
        !hasAllUserProperties.value ? control.computedLabel.value || 'Contact details' : 'Insurance details'
      );

      function capitaliseName() {
        user.value.firstName = capitalise(user.value.firstName || '');
        user.value.lastName = capitalise(user.value.lastName || '');
      }

      function validateEmailAndSetError(email?: string) {
        if (validateEmail(email)) {
          emailError.value = '';
          return;
        }
        emailError.value = 'Email must be a valid email address';
      }

      function phoneChange(phone: Phone) {
        user.value.phone = phone;
        validatePhoneAgainstDriver(phone);
      }

      function validatePhoneAgainstDriver(phone: Phone) {
        if (!phone?.formatted) {
          phoneMatchError.value = '';
          return;
        }

        const rootData = jsonforms?.core?.data;
        const driverData = rootData?.driver;

        if (Array.isArray(driverData) && driverData.length > 0) {
          const selectedDriver = driverData.find((d: UserConfig) => d && d.selected === true);
          if (selectedDriver) {
            const driverPhone = selectedDriver.phone?.formatted || selectedDriver.phone;
            if (driverPhone && phone.formatted === driverPhone) {
              phoneMatchError.value = 'Account contact phone number cannot be the same as the driver phone number';
              errors.value['contact-phone'] = phoneMatchError.value;
              return;
            }
          }
        }

        phoneMatchError.value = '';
        errors.value['contact-phone'] = '';
      }

      function getFormattedPhone(phone?: Phone | string): Phone {
        if (!phone) {
          return {
            formatted: '',
          };
        }
        if (typeof phone === 'string') {
          return {
            formatted: phone,
          };
        }
        return phone;
      }
      function updateData(key?: 'user' | 'insurance') {
        nextTick(() => {
          const userData = {
            firstName: user.value.firstName || undefined,
            lastName: user.value.lastName || undefined,
            email: user.value.email || undefined,
            phone: {
              formatted: user.value.phone.formatted || undefined,
              countryCallingCode: user.value.phone.countryCallingCode || undefined,
              nationalNumber: user.value.phone.nationalNumber || undefined,
            },
          };
          control.onChange({
            selectedUser: userData,
            selectedInsurance: {
              provider: insurance.value.provider || undefined,
              number: insurance.value.number || undefined,
            },
            ...(key
              ? {
                  isModified:
                    controlData.value?.isModified?.length && !controlData.value.isModified.includes(key)
                      ? controlData.value.isModified.concat([key])
                      : [key],
                }
              : { isModified: controlData.value?.isModified }),
          });
          sendAnalytics('set', 'user_properties', {
            meta: userData,
            theme: config.value.clientTheme,
            clientId: config.value.clientId,
          });
          setUserData(user.value);

          if (userData?.phone?.formatted && config.value?.selectedClaim && userData.firstName) {
            const clarity = getClarity();
            if (clarity) {
              clarity(
                'identify',
                userData.phone?.formatted,
                config.value.sessionData?.id,
                config.value.selectedClaim,
                `${userData?.firstName || 'Unknown name'} ${userData?.lastName || ''}`
              );
            }
          }
        });
      }
      onMounted(() => {
        updateData();
      });

      watch(
        () => [config.value.insurance, config.value.driver],
        () => {
          nextTick(() => {
            if (config.value.driver) {
              user.value = {
                ...user.value,
                ...config.value.driver,
                phone: {
                  formatted:
                    typeof config.value.driver?.phone === 'string'
                      ? config.value.driver?.phone || user.value.phone.formatted || ''
                      : config.value.driver?.phone?.formatted || user.value.phone.formatted || '',
                },
              };
            }
            if (config.value.insurance) {
              insurance.value = {
                ...insurance.value,
                ...{
                  provider: config.value?.insurance?.provider,
                  number: config.value?.insurance?.number,
                },
              };
            }
            sendAnalytics('set', 'user_properties', {
              meta: user.value,
            });
          });
        },
        { deep: true }
      );

      function isRequiredProperty(property: keyof UserProperty) {
        return requiredUserProperties.value.includes(property);
      }
      return {
        user,
        insurance,
        title,
        hasAllUserProperties,
        hasAllInsuranceProperties,
        hasAllProperties,
        emailError,
        phoneFormatError,
        isRequiredProperty,
        phoneError,
        licensePlateError,
        clientConfig: config,
        controlOptions,
        showErrors,
        ...control,
        capitaliseName,
        validateEmail: validateEmailAndSetError,
        phoneChange,
        updateData,
      };
    },
  });
</script>
